'use client';

import { useState, useEffect } from 'react';
import { Key, Copy, Plus, Trash2, Shield } from 'lucide-react';
import { useDeveloper } from '@/hooks/useDeveloper';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { Skeleton } from '@/components/ui/skeleton';

export function ApiKeysPage() {
  const { apiKeys, loading, fetchApiKeys, createApiKey, revokeApiKey } = useDeveloper();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
      const rawKey = await createApiKey({
      name: newKeyName,
      permissions: ['read', 'write'],
    });
    if (rawKey) {
      setCreatedKey(rawKey);
      setCreateDialogOpen(false);
      setNewKeyName('');
      toast.success('API key created successfully');
    }
  };

  const handleCopy = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('Copied to clipboard');
  };

  const handleRevoke = async (id: string) => {
    const success = await revokeApiKey(id);
    if (success) {
      toast.success('API key revoked');
    }
  };

  if (loading && apiKeys.length === 0) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card className="glass">
          <CardContent className="pt-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">API Keys</h1>
          <p className="text-slate-400 mt-1">Manage your API keys for programmatic access</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Create API Key
            </Button>
          </DialogTrigger>
          <DialogContent className="glass max-w-md">
            <DialogHeader>
              <DialogTitle>Create New API Key</DialogTitle>
              <CardDescription>
                Give your API key a name to identify it later
              </CardDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="key-name">Key Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., Production Server"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate}>
                Create Key
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {createdKey && (
        <Card className="glass border-yellow-500/30">
          <CardHeader>
            <CardTitle className="text-yellow-400">New API Key Created</CardTitle>
            <CardDescription>
              Store this key securely. You won't be able to see it again.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-900/50 p-3 rounded-lg font-mono text-sm break-all">
                {createdKey}
              </code>
              <Button size="sm" variant="ghost" onClick={() => handleCopy(createdKey)}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {apiKeys.length === 0 ? (
        <Card className="glass">
          <CardContent className="pt-6 text-center py-12">
            <Key className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No API keys yet</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              Create Your First API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {apiKeys.map((apiKey) => (
            <Card key={apiKey.id} className="glass">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="h-4 w-4 text-cyan-400" />
                      <p className="font-medium text-white">{apiKey.name}</p>
                      <Badge variant={apiKey.revoked ? 'destructive' : 'default'}>
                        {apiKey.revoked ? 'Revoked' : 'Active'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 font-mono">
                      {apiKey.keyPrefix}...{apiKey.keySuffix}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Created {new Date(apiKey.createdAt).toLocaleDateString()} • Last used {apiKey.lastUsedAt ? new Date(apiKey.lastUsedAt).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                  {!apiKey.revoked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:text-red-300"
                      onClick={() => handleRevoke(apiKey.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
